/**
 * Stage IG redesign Phase 2:DM 管理(spec §7)。
 *
 * 三栏レイアウト(spec §7.1):
 *   - 左 310px:受信トレイ(Segmented Control + 検索 + 会話一覧)
 *   - 中央 minmax(0,1fr):ヘッダ + メッセージスレッド + Composer
 *   - 右 290px:顧客情報 + 対応情報
 *
 * P1-A 互換:
 *   - 会話切替 / status 絞り込み / 検索 / メッセージ表示
 *   - Capability エラーは banner 化(spec §7.5)
 *   - Composer 内の P1-C 未実装操作は disabled で PENDING_FEATURE_HINT 表示
 */

import { useEffect, useMemo, useState } from "react";
import {
  Image as ImageIcon,
  MessageSquare,
  Search,
  Send,
  Settings as SettingsIcon,
  Smile,
  Sparkles,
  StickyNote,
  Zap,
} from "lucide-react";

import {
  AccountChip,
  EmptyState,
  ErrorState,
  InlineWarningBanner,
  InstagramSubnav,
  LoadingState,
  RefreshButton,
  SectionCard,
  SegmentedControl,
  STATUS_LABEL,
  StatusBadge,
} from "./components/common";
import {
  useInstagramCapabilities,
  useInstagramConnection,
  useInstagramConversation,
  useInstagramConversations,
} from "./hooks/useInstagram";
import type { ConversationListItem, ConversationMessage, WorkflowStatus } from "./types";
import {
  formatDateHeading,
  formatNumber,
  formatRelativeTime,
  formatTimeOnly,
  getInitial,
} from "./utils/format";
import "./instagram.css";

const PENDING_FEATURE_HINT = "この操作は次のステップ（P1-C）で有効になります。";

type StatusFilterKey = "all" | "pending" | "in_progress" | "done";

const STATUS_FILTERS: { key: StatusFilterKey; label: string }[] = [
  { key: "all", label: "すべて" },
  { key: "pending", label: "未対応" },
  { key: "in_progress", label: "対応中" },
  { key: "done", label: "完了" },
];

// ---------------------------------------------------------------------------
// 会話行(spec §7.2)
// ---------------------------------------------------------------------------

function ConversationRow({
  item,
  active,
  onSelect,
}: {
  item: ConversationListItem;
  active: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      type="button"
      className={`ig-conversation-item${active ? " is-selected" : ""}`}
      onClick={() => onSelect(item.id)}
    >
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: "50%",
          background: "var(--ig-surface-soft)",
          color: "var(--ig-text)",
          display: "grid",
          placeItems: "center",
          fontWeight: 600,
          overflow: "hidden",
        }}
      >
        {item.avatar_url ? (
          <img
            src={item.avatar_url}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          getInitial(item.display_name || item.user_name)
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--ig-text)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.display_name || item.user_name}
          </span>
          <span style={{ fontSize: 10, color: "var(--ig-muted)", flexShrink: 0 }}>
            {formatRelativeTime(item.updated_at)}
          </span>
        </div>
        <div
          style={{
            marginTop: 3,
            fontSize: 12,
            color: "var(--ig-muted)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.preview}
        </div>
        <div
          style={{
            marginTop: 6,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <StatusBadge status={item.status} />
          {item.unread_count > 0 ? (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#fff",
                background: "var(--ig-primary-2)",
                padding: "1px 6px",
                borderRadius: 999,
              }}
            >
              {item.unread_count > 99 ? "99+" : item.unread_count}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// メッセージスレッド(spec §7.3)
// ---------------------------------------------------------------------------

function groupByDate(messages: ConversationMessage[]) {
  const out: { heading: string; items: ConversationMessage[] }[] = [];
  for (const m of messages) {
    const heading = formatDateHeading(m.created_at);
    const last = out[out.length - 1];
    if (last && last.heading === heading) last.items.push(m);
    else out.push({ heading, items: [m] });
  }
  return out;
}

function MessageThread({ messages }: { messages: ConversationMessage[] }) {
  const groups = useMemo(() => groupByDate(messages), [messages]);

  if (messages.length === 0) {
    return (
      <div className="ig-empty-state">
        <div>
          <div style={{ fontSize: 13 }}>このスレッドにはまだメッセージがありません</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {groups.map((group) => (
        <div key={group.heading}>
          <div
            style={{
              textAlign: "center",
              margin: "8px 0 12px",
              fontSize: 11,
              color: "var(--ig-muted)",
            }}
          >
            <span
              style={{
                background: "#fff",
                padding: "2px 10px",
                border: "1px solid var(--ig-border)",
                borderRadius: 999,
              }}
            >
              {group.heading}
            </span>
          </div>
          {group.items.map((m) => (
            <div
              key={m.id}
              style={{
                display: "flex",
                justifyContent:
                  m.direction === "outbound" ? "flex-end" : "flex-start",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <div
                className={`ig-message-bubble ig-message-bubble--${
                  m.direction === "outbound" ? "outgoing" : "incoming"
                }`}
              >
                {m.text}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--ig-muted)",
                  alignSelf: "flex-end",
                  flexShrink: 0,
                }}
              >
                {formatTimeOnly(m.created_at)}
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function InstagramInboxPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilterKey>("all");
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftText, setDraftText] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const list = useInstagramConversations({
    status: statusFilter === "all" ? undefined : statusFilter,
    q: query,
  });
  const detail = useInstagramConversation(selectedId);
  const caps = useInstagramCapabilities();
  const conn = useInstagramConnection();

  const items = list.data?.items ?? [];
  const counts = list.data?.counts ?? {};
  const canReply = caps.data?.capabilities.messaging_reply ?? false;
  const messagingUnsupported =
    !!caps.errorCode && caps.errorCode === "AUTH_IG_CAPABILITY_NOT_SUPPORTED";

  useEffect(() => {
    if (items.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !items.some((i) => i.id === selectedId)) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId]);

  useEffect(() => {
    setDraftText("");
  }, [selectedId]);

  const current = detail.data;

  const accountName =
    conn.data?.connection?.account_name ??
    (conn.data?.is_mock ? "portal_shoji_official" : null);

  return (
    <div className="ig-page">
      <InboxHero
        accountName={accountName}
        connected={Boolean(
          conn.data?.status === "connected" || conn.data?.is_mock,
        )}
        onRefresh={() => {
          list.refresh();
          detail.refresh();
          conn.refresh();
        }}
        loading={list.loading}
      />

      {messagingUnsupported ? (
        <InlineWarningBanner
          title="DM機能を利用できません"
          description="Facebook Pageとの連携、または追加設定が必要です。接続設定で権限を確認してください。"
          actionLabel="接続設定を確認"
          onAction={() => {
            window.location.href = "/instagram/settings";
          }}
          debugCode={caps.errorCode}
        />
      ) : null}

      {caps.error && !messagingUnsupported ? (
        <ErrorState message={caps.error} code={caps.errorCode} onRetry={caps.refresh} />
      ) : null}

      <div className="ig-inbox">
        {/* ---- 左:会話リスト ---- */}
        <div className="ig-inbox__aside">
          <div
            style={{
              padding: "16px 18px 12px",
              borderBottom: "1px solid var(--ig-divider)",
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "var(--ig-text)",
                marginBottom: 10,
              }}
            >
              受信トレイ
            </div>
            <SegmentedControl
              items={STATUS_FILTERS}
              value={statusFilter}
              onChange={setStatusFilter}
            />
            <div
              style={{
                position: "relative",
                marginTop: 12,
              }}
            >
              <Search
                size={14}
                style={{
                  position: "absolute",
                  top: 13,
                  left: 10,
                  color: "var(--ig-muted)",
                }}
              />
              <input
                className="ig-input"
                placeholder="ユーザー名・メッセージで検索"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ paddingLeft: 30 }}
              />
            </div>
          </div>

          <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
            {list.error ? (
              <ErrorState
                message={list.error}
                code={list.errorCode}
                onRetry={list.refresh}
              />
            ) : list.loading && items.length === 0 ? (
              <div style={{ padding: 14 }}>
                {Array.from({ length: 6 }, (_, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      padding: "12px 14px",
                      borderBottom: "1px solid var(--ig-divider)",
                    }}
                  >
                    <div
                      className="ig-skeleton"
                      style={{ width: 42, height: 42, borderRadius: 999 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div
                        className="ig-skeleton ig-skeleton-line"
                        style={{ width: "60%", marginBottom: 6 }}
                      />
                      <div
                        className="ig-skeleton ig-skeleton-line"
                        style={{ width: "85%" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : items.length === 0 ? (
              <EmptyState
                title="会話がありません"
                description={
                  query || statusFilter !== "all"
                    ? "条件に一致する会話は見つかりませんでした。"
                    : "新しいDMが届くとここに表示されます。"
                }
              />
            ) : (
              items.map((item) => (
                <ConversationRow
                  key={item.id}
                  item={item}
                  active={item.id === selectedId}
                  onSelect={setSelectedId}
                />
              ))
            )}
          </div>
        </div>

        {/* ---- 中央:メッセージスレッド ---- */}
        <div className="ig-inbox__thread">
          {!selectedId ? (
            <EmptyState
              title="会話を選択してください"
              description="左のリストから会話を選ぶと、メッセージ履歴が表示されます。"
            />
          ) : detail.error ? (
            <ErrorState
              message={detail.error}
              code={detail.errorCode}
              onRetry={detail.refresh}
            />
          ) : detail.loading && !current ? (
            <LoadingState label="メッセージを読み込んでいます..." />
          ) : !current ? (
            <EmptyState description="この会話を表示できませんでした。" />
          ) : (
            <>
              <div
                style={{
                  padding: "16px 22px",
                  borderBottom: "1px solid var(--ig-divider)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "var(--ig-surface-soft)",
                      color: "var(--ig-text)",
                      display: "grid",
                      placeItems: "center",
                      fontWeight: 600,
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    {current.avatar_url ? (
                      <img
                        src={current.avatar_url}
                        alt=""
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      getInitial(current.display_name || current.user_name)
                    )}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--ig-text)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {current.display_name || current.user_name}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ig-muted)" }}>
                      @{current.user_name}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <StatusBadge status={current.status} />
                  <button
                    type="button"
                    className="ig-button"
                    style={{ height: 32 }}
                    disabled
                    title={PENDING_FEATURE_HINT}
                  >
                    <SettingsIcon size={13} />
                    操作
                  </button>
                </div>
              </div>

              <div className="ig-message-list" style={{ flex: 1, minHeight: 0 }}>
                <MessageThread messages={current.messages} />
              </div>

              {/* Composer(spec §7.3) */}
              <div
                style={{
                  padding: "12px 22px 16px",
                  borderTop: "1px solid var(--ig-divider)",
                  background: "var(--ig-surface)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    marginBottom: 8,
                  }}
                >
                  <button
                    type="button"
                    className="ig-button"
                    style={{ height: 30, fontSize: 12 }}
                    disabled
                    title={PENDING_FEATURE_HINT}
                  >
                    <Zap size={12} />
                    クイック返信
                  </button>
                  <button
                    type="button"
                    className="ig-button"
                    style={{ height: 30, fontSize: 12 }}
                    disabled
                    title={PENDING_FEATURE_HINT}
                  >
                    <Sparkles size={12} />
                    AI返信提案
                  </button>
                  <button
                    type="button"
                    className="ig-button"
                    style={{ height: 30, fontSize: 12 }}
                    disabled
                    title={PENDING_FEATURE_HINT}
                  >
                    <StickyNote size={12} />
                    社内メモ
                  </button>
                </div>

                <textarea
                  className="ig-textarea"
                  placeholder={
                    canReply
                      ? "メッセージを入力..."
                      : "現在の接続方式では返信を送信できません。"
                  }
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  disabled={!canReply}
                  style={{ minHeight: 72, fontSize: 13 }}
                />

                <div
                  style={{
                    marginTop: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <button
                      type="button"
                      className="ig-button ig-button--ghost"
                      style={{ height: 30, width: 30, padding: 0 }}
                      disabled
                      title={PENDING_FEATURE_HINT}
                    >
                      <ImageIcon size={14} />
                    </button>
                    <button
                      type="button"
                      className="ig-button ig-button--ghost"
                      style={{ height: 30, width: 30, padding: 0 }}
                      disabled
                      title={PENDING_FEATURE_HINT}
                    >
                      <Smile size={14} />
                    </button>
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--ig-muted)",
                        marginLeft: 4,
                      }}
                    >
                      {PENDING_FEATURE_HINT}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="ig-button ig-button--primary"
                    disabled
                    title={PENDING_FEATURE_HINT}
                  >
                    <Send size={14} />
                    送信
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ---- 右:顧客情報 / 対応情報(spec §7.4) ---- */}
        <div className="ig-inbox__panel">
          {!current ? (
            <div
              style={{
                padding: 32,
                textAlign: "center",
                color: "var(--ig-muted)",
                fontSize: 12,
              }}
            >
              <MessageSquare
                size={28}
                style={{ marginBottom: 10, color: "var(--ig-muted)" }}
              />
              <div>会話を選択すると顧客情報が表示されます</div>
            </div>
          ) : (
            <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
              <SectionCard title="顧客情報">
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: "50%",
                      background: "var(--ig-surface-soft)",
                      color: "var(--ig-text)",
                      display: "grid",
                      placeItems: "center",
                      fontWeight: 700,
                      fontSize: 18,
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    {current.participant.avatar_url ? (
                      <img
                        src={current.participant.avatar_url}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      getInitial(current.participant.display_name)
                    )}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--ig-text)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {current.participant.display_name}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ig-muted)" }}>
                      @{current.participant.user_name}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 14,
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 8,
                  }}
                >
                  <Stat label="フォロワー" value={formatNumber(current.participant.followers_count)} />
                  <Stat label="フォロー中" value={formatNumber(current.participant.follows_count)} />
                  <Stat label="投稿" value={formatNumber(current.participant.media_count)} />
                </div>
              </SectionCard>

              <SectionCard title="対応情報">
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <Field label="ステータス">
                    <select
                      className="ig-select"
                      value={current.status}
                      disabled
                      title={PENDING_FEATURE_HINT}
                    >
                      {(Object.keys(STATUS_LABEL) as WorkflowStatus[])
                        .filter((s) => s !== "spam")
                        .map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABEL[s]}
                          </option>
                        ))}
                    </select>
                  </Field>
                  <Field label="担当者">
                    <div
                      style={{
                        fontSize: 13,
                        color: current.assignee_user_id
                          ? "var(--ig-text)"
                          : "var(--ig-muted)",
                      }}
                    >
                      {current.assignee_user_id ?? "未割り当て"}
                    </div>
                  </Field>
                  <Field label="内部メモ">
                    <textarea
                      className="ig-textarea"
                      placeholder="社内向けのメモを記録できます"
                      defaultValue={current.internal_note ?? ""}
                      disabled
                      title={PENDING_FEATURE_HINT}
                      style={{ minHeight: 60, fontSize: 12 }}
                    />
                  </Field>
                  <div
                    style={{ fontSize: 11, color: "var(--ig-muted)" }}
                  >
                    {PENDING_FEATURE_HINT}
                  </div>
                </div>
              </SectionCard>

              <SectionCard title="対応履歴">
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--ig-muted)",
                    textAlign: "center",
                    padding: "12px 0",
                  }}
                >
                  最終更新: {formatRelativeTime(current.updated_at)}
                </div>
              </SectionCard>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero(spec §5)
// ---------------------------------------------------------------------------

function InboxHero({
  accountName,
  connected,
  onRefresh,
  loading,
}: {
  accountName: string | null;
  connected: boolean;
  onRefresh: () => void;
  loading?: boolean;
}) {
  return (
    <div className="ig-module-hero">
      <div className="ig-module-hero__row">
        <div>
          <div className="ig-module-hero__title">DM管理</div>
          <div className="ig-module-hero__subtitle">
            顧客とのやり取りと対応状況を一元管理
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <AccountChip accountName={accountName} connected={connected} />
          <RefreshButton onClick={onRefresh} loading={loading} />
        </div>
      </div>
      <InstagramSubnav variant="hero" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 小的内部组件
// ---------------------------------------------------------------------------

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: "var(--ig-surface-soft)",
        borderRadius: 10,
        padding: "8px 10px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: "var(--ig-text)",
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 10, color: "var(--ig-muted)", marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: "var(--ig-muted)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}